import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import { UserService } from 'app';
import { UserStatisticsResponse, UserStatisticsPeriodData, UserStatisticsLevelData } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-statistics-page',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    TableDirective,
    TranslatePipe
  ],
  templateUrl: './user-statistics-page.component.html',
  styleUrls: ['./user-statistics-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserStatisticsPageComponent {
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('UserStatisticsPageComponent');

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  statisticsData = signal<UserStatisticsResponse | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  // ────────────────────────────────────────────────
  // Computed properties
  // ────────────────────────────────────────────────
  periods = computed(() => this.statisticsData()?.periods || []);

  activeLevels = computed(() => {
    const data = this.statisticsData();
    if (!data) return [];

    const levelsWithData = new Set<number>();

    data.periods.forEach(period => {
      Object.entries(period.levels).forEach(([levelStr, levelData]) => {
        if (levelData.total > 0) {
          levelsWithData.add(Number(levelStr));
        }
      });
    });

    return Array.from(levelsWithData).sort((a, b) => a - b);
  });

  // Period labels — reactive to language changes
  periodLabels = computed<Record<string, string>>(() => ({
    total: this.translate.translate('userStatistics.periods.total'),
    day_90: this.translate.translate('userStatistics.periods.last90Days'),
    day_30: this.translate.translate('userStatistics.periods.last30Days'),
    day_14: this.translate.translate('userStatistics.periods.last14Days'),
    day_7: this.translate.translate('userStatistics.periods.last7Days'),
    day_1: this.translate.translate('userStatistics.periods.last24Hours')
  }));

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Load data on init
    this.loadStatistics();

    // Reload on language change
    effect((onCleanup) => {
      const sub = this.translate.langChanges.subscribe(() => {
        this.logger.debug('Language changed, reloading statistics');
        this.loadStatistics();
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  loadStatistics(): void {
    this.loading.set(true);
    this.error.set(null);

    this.userService.getUserStatistics()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.logger.debug('Statistics loaded successfully', data);
            this.statisticsData.set(data);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Error loading statistics', err);
            this.error.set('Failed to load user statistics');
            this.loading.set(false);
          }
        });
  }

  // ────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────
  getPeriodLabel(period: string): string {
    const labels = this.periodLabels();
    return labels[period] || period;
  }

  getLevelData(periodData: UserStatisticsPeriodData, level: number): UserStatisticsLevelData {
    return periodData.levels[level] || { active: 0, inactive: 0, unconfirmed: 0, total: 0 };
  }

  getTotalForPeriod(periodData: UserStatisticsPeriodData): { active: number; inactive: number; unconfirmed: number; total: number } {
    const totals = { active: 0, inactive: 0, unconfirmed: 0, total: 0 };

    Object.values(periodData.levels).forEach(levelData => {
      totals.active += levelData.active;
      totals.inactive += levelData.inactive;
      totals.unconfirmed += levelData.unconfirmed;
      totals.total += levelData.total;
    });

    return totals;
  }

  getStatusClass(count: number): string {
    if (count === 0) return 'text-muted';
    if (count < 5) return 'text-primary';
    if (count < 10) return 'text-primary';
    return 'text-primary fw-bold';
  }
}